import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import type { EstatutoDestino } from './useEstatuto';
import { SlaInfo } from '../components/kanban/SlaBadge';

// Tipos
export type KanbanPrioridade = 'baixa' | 'media' | 'alta';
export type CampoTipo = 'texto' | 'numero' | 'moeda' | 'data' | 'select' | 'checkbox';

// Papel de um usuário no vínculo com o card (espelha o backend #762).
export type KanbanPapel = 'responsavel' | 'supervisor';

// Interfaces
export interface IKanbanUsuario {
  id: number;
  nome: string | null;
  // Papel no card. Default 'responsavel' p/ compat com payloads antigos.
  papel?: KanbanPapel;
}

// Vínculo card↔usuário com papel (substitui o user_ids plano no create/update).
export interface IKanbanVinculo {
  user_id: number;
  papel: KanbanPapel;
}

export interface IKanbanCampoValor {
  campo_id: number;
  campo_nome: string;
  tipo: CampoTipo;
  valor: string | number | boolean | null;
}

export interface IKanbanCampoValorInput {
  campo_id: number;
  valor_texto?: string | null;
  valor_numero?: number | null;
  valor_data?: string | null;
  valor_boolean?: boolean | null;
}

export interface IKanbanCampoDefinicao {
  id: number;
  projeto_id: number;
  nome: string;
  tipo: CampoTipo;
  opcoes: string[] | null;
  obrigatorio: boolean;
  ordem: number;
  dt_criacao?: string;
}

export interface IKanbanCardServico {
  id: number;
  nome: string;
  categoria_cor: string | null;
}

export interface IKanbanCard {
  id: number;
  titulo: string;
  descricao: string | null;
  rank: string;
  prioridade: KanbanPrioridade;
  expira_em: string | null;
  moved_at: string | null;
  dt_criacao: string;
  dt_atualizacao: string;
  // `usuarios` = responsáveis (compat); supervisores vêm separados.
  usuarios: IKanbanUsuario[];
  supervisores?: IKanbanUsuario[];
  criado_por: number;
  campo_valores: IKanbanCampoValor[];
  servicos: IKanbanCardServico[];
  anexos_count: number;
  has_pending_autentique_doc?: boolean;
  assinatura_rejeitada?: boolean;
  // SLA / tempo (#761) — calculados no backend; `sla` null = card sem expira_em.
  feriados?: string[] | null;
  sla?: SlaInfo | null;
  dias_etapa_atual?: number | null;
  dias_total?: number | null;
}

export interface IKanbanColumn {
  id: number;
  titulo: string;
  rank: string;
  projeto_id: number | null;
  dt_criacao: string;
  dt_atualizacao: string;
  cards: IKanbanCard[];
  protegida?: boolean;
}

export interface IKanbanDepartamento {
  id: number;
  nome: string;
}

export interface IKanbanProject {
  id: number;
  nome?: string;
  titulo?: string;
  descricao: string | null;
  cor?: string | null;
  coluna_finalizado_id?: number | null;
  evento_id?: number | null;
  // Origem do quadro (task #757): null = avulso; 'estatutos'/'eventos' = módulo.
  // Usado p/ separar o /kanban em 2 faixas (avulsos x espelho de módulo).
  origem_modulo?: string | null;
  dt_criacao: string;
  dt_atualizacao: string;
  departamentos?: IKanbanDepartamento[];
}

export interface IKanbanBoard {
  columns: IKanbanColumn[];
}

export interface IKanbanResponse<T> {
  message: string;
  meta?: object;
  data: T;
}

export interface IKanbanCardArquivado {
  id: number;
  titulo: string;
  descricao: string | null;
  prioridade: 'baixa' | 'media' | 'alta';
  coluna_id: number;
  coluna_titulo: string;
  projeto_id: number;
  arquivado_em: string;
  /** Motivo informado ao arquivar. `null` em cards arquivados antes do recurso. */
  motivo?: string | null;
  /** Nome de quem arquivou (vem da atividade do card). */
  arquivado_por?: string | null;
}

export interface IColunaPerm {
  avancar_para: boolean;
  retroceder_para: boolean;
  criar_card: boolean;
}

export type IColunaPermMap = Record<string, IColunaPerm>;

// Project types
export interface ICreateProjectData {
  titulo: string;
  descricao?: string;
  cor?: string | null;
  departamento_ids?: number[];
  coluna_finalizado_id?: number | null;
}

export interface IUpdateProjectData {
  id: number;
  nome?: string;
  descricao?: string;
  cor?: string | null;
  departamento_ids?: number[];
  coluna_finalizado_id?: number | null;
}

// Column types
export interface ICreateColumnData {
  titulo: string;
  projeto_id?: number;
  prev_rank?: string | null;
  next_rank?: string | null;
}

export interface IUpdateColumnData {
  id: number;
  titulo?: string;
  projeto_id?: number;
}

export interface IMoveColumnData {
  id: number;
  prev_rank?: string | null;
  next_rank?: string | null;
}

// Card types
export interface ICreateCardData {
  titulo: string;
  descricao?: string;
  column_id: number;
  prioridade?: KanbanPrioridade;
  expira_em?: string | null;
  // Vínculos com papel (novo). Pelo menos 1 responsável.
  vinculos?: IKanbanVinculo[];
  // [DEPRECADO] tratados como responsáveis no backend. Use `vinculos`.
  user_ids?: number[] | null;
  prev_rank?: string | null;
  next_rank?: string | null;
  campo_valores?: IKanbanCampoValorInput[];
}

export interface IUpdateCardData {
  id: number;
  titulo?: string;
  descricao?: string;
  prioridade?: KanbanPrioridade;
  expira_em?: string | null;
  // Motivo da mudança de prazo, para a linha da aba Atividade (quadro da legalização).
  motivo_prazo?: string;
  // Vínculos com papel. Omitir = não mexe; enviar = substitui (mantém ≥1 resp.).
  vinculos?: IKanbanVinculo[];
  // [DEPRECADO] use `vinculos`.
  user_ids?: number[] | null;
  campo_valores?: IKanbanCampoValorInput[];
  // "Aguardando cliente" (T6.1 #766): marca/desmarca; motivo_bloqueio obrigatório ao marcar.
  aguardando_cliente?: boolean;
  motivo_bloqueio?: string | null;
}

export interface IMoveCardData {
  id: number;
  column_id: number;
  prev_rank?: string | null;
  next_rank?: string | null;
}

// User option
export interface IKanbanUserOption {
  id: number;
  nome: string;
  email: string;
  imagem_url?: string | null;
}

// Comment types
export interface IKanbanCommentUser {
  id: number;
  nome: string;
}

export interface IKanbanComment {
  id: number;
  descricao: string;
  card_id: number;
  user_id: number;
  usuario: IKanbanCommentUser;
  dt_criacao: string;
  dt_atualizacao: string;
}

export interface ICreateCommentData {
  descricao: string;
}

export interface IKanbanAnexo {
  id: number;
  nome: string;
  s3_key: string;
  content_type: string | null;
  tamanho: number | null;
  dt_criacao: string;
  usuario_nome: string | null;
  url: string;
  autentique_status?: string | null;
}

export interface IKanbanAtividade {
  id: number;
  acao: string;
  descricao: string;
  usuario: { id: number; nome: string } | null;
  dt_criacao: string;
}

// Campo definition types
export interface ICreateCampoData {
  nome: string;
  tipo: CampoTipo;
  opcoes?: string[];
  obrigatorio?: boolean;
}

export interface IUpdateCampoData {
  nome?: string;
  opcoes?: string[];
  obrigatorio?: boolean;
}

export const useKanban = () => {
  // Projects
  const getProjects = async (
    key?: QueryFunctionContext,
  ): Promise<IKanbanResponse<IKanbanProject[]>> => {
    const filters = (key?.queryKey?.[1] ?? {}) as {
      departamento_id?: number | null;
      apenas_eventos?: boolean;
    };
    const params: Record<string, number | boolean> = {};
    if (filters.apenas_eventos) {
      params.apenas_eventos = true;
    } else if (filters.departamento_id != null) {
      params.departamento_id = filters.departamento_id;
    }
    const res = await api.get<IKanbanResponse<IKanbanProject[]>>('/api/v1/kanban/projetos/', {
      params,
    });
    return res.data;
  };

  // Resolve o id do projeto Kanban do módulo Estatutos pelo endpoint dedicado
  // (task #757) — em vez de procurar por título na lista geral de projetos, que
  // era frágil (um quadro avulso chamado "Estatutos" roubaria a resolução).
  const getEstatutoProjeto = async (
    destino: EstatutoDestino = 'constituicao',
  ): Promise<IKanbanResponse<{ projeto_id: number }>> => {
    const res = await api.get<IKanbanResponse<{ projeto_id: number }>>(
      '/api/v1/estatuto/kanban-projeto',
      { params: { destino } },
    );
    return res.data;
  };

  const createProject = async (
    data: ICreateProjectData,
  ): Promise<IKanbanResponse<IKanbanProject>> => {
    const res = await api.post<IKanbanResponse<IKanbanProject>>('/api/v1/kanban/projetos/', data);
    return res.data;
  };

  const updateProject = async (
    data: IUpdateProjectData,
  ): Promise<IKanbanResponse<IKanbanProject>> => {
    const { id, ...updateData } = data;
    const res = await api.put<IKanbanResponse<IKanbanProject>>(
      `/api/v1/kanban/projetos/${id}/`,
      updateData,
    );
    return res.data;
  };

  const archiveProject = async (
    projectId: number,
  ): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.delete<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/projetos/${projectId}/`,
    );
    return res.data;
  };

  // Board
  const getBoard = async (
    projectId?: number,
    ordenarPorPrioridade?: boolean,
    filtroCampos?: Record<string | number, unknown>,
  ): Promise<IKanbanResponse<IKanbanBoard>> => {
    const params: Record<string, unknown> = {};
    if (projectId) params.projeto_id = projectId;
    if (ordenarPorPrioridade) params.ordenar_por_prioridade = true;
    if (filtroCampos && Object.keys(filtroCampos).length > 0) {
      params.filtro_campos = JSON.stringify(filtroCampos);
    }
    const res = await api.get<IKanbanResponse<IKanbanBoard>>('/api/v1/kanban/', { params });
    return res.data;
  };

  // Columns
  const createColumn = async (data: ICreateColumnData): Promise<IKanbanResponse<IKanbanColumn>> => {
    const res = await api.post<IKanbanResponse<IKanbanColumn>>('/api/v1/kanban/columns/', data);
    return res.data;
  };

  const updateColumn = async (data: IUpdateColumnData): Promise<IKanbanResponse<IKanbanColumn>> => {
    const { id, ...updateData } = data;
    const res = await api.put<IKanbanResponse<IKanbanColumn>>(
      `/api/v1/kanban/columns/${id}/`,
      updateData,
    );
    return res.data;
  };

  const moveColumn = async (data: IMoveColumnData): Promise<IKanbanResponse<IKanbanColumn>> => {
    const { id, ...moveData } = data;
    const res = await api.put<IKanbanResponse<IKanbanColumn>>(
      `/api/v1/kanban/columns/${id}/move/`,
      moveData,
    );
    return res.data;
  };

  const archiveColumn = async (columnId: number): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.delete<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/columns/${columnId}/`,
    );
    return res.data;
  };

  // Cards
  const createCard = async (data: ICreateCardData): Promise<IKanbanResponse<IKanbanCard>> => {
    const res = await api.post<IKanbanResponse<IKanbanCard>>('/api/v1/kanban/cards/', data);
    return res.data;
  };

  const updateCard = async (data: IUpdateCardData): Promise<IKanbanResponse<IKanbanCard>> => {
    const { id, ...updateData } = data;
    const res = await api.put<IKanbanResponse<IKanbanCard>>(
      `/api/v1/kanban/cards/${id}/`,
      updateData,
    );
    return res.data;
  };

  const moveCard = async (data: IMoveCardData): Promise<IKanbanResponse<IKanbanCard>> => {
    const { id, ...moveData } = data;
    const res = await api.put<IKanbanResponse<IKanbanCard>>(
      `/api/v1/kanban/cards/${id}/move/`,
      moveData,
    );
    return res.data;
  };

  // Exclui o card de vez (≠ arquivar).
  const archiveCard = async (cardId: number): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.delete<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/cards/${cardId}/`,
    );
    return res.data;
  };

  // Arquiva o card: some do board, preserva a posição (≠ excluir).
  // `motivo` é opcional e vai para a trilha de atividade do card (com quem/quando).
  const arquivarCard = async (
    cardId: number,
    motivo?: string,
  ): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.post<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/cards/${cardId}/arquivar/`,
      motivo ? { motivo } : {},
    );
    return res.data;
  };

  // Desarquiva o card: volta pro board na posição preservada.
  const desarquivarCard = async (cardId: number): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.post<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/cards/${cardId}/desarquivar/`,
    );
    return res.data;
  };

  const getCardsArquivados = async (
    projetoId: number,
  ): Promise<IKanbanResponse<IKanbanCardArquivado[]>> => {
    const res = await api.get<IKanbanResponse<IKanbanCardArquivado[]>>(
      `/api/v1/kanban/cards/arquivados/`,
      { params: { projeto_id: projetoId } },
    );
    return res.data;
  };

  // Users
  const getUsersByDepartamentos = async (
    departamentoIds: number[],
  ): Promise<IKanbanResponse<IKanbanUserOption[]>> => {
    const params = new URLSearchParams();
    departamentoIds.forEach(id => params.append('departamento_ids', String(id)));
    const res = await api.get<IKanbanResponse<IKanbanUserOption[]>>(
      `/api/v1/usuarios/by-departamentos/?${params.toString()}`,
    );
    return res.data;
  };

  // Comments
  const getComments = async (cardId: number): Promise<IKanbanResponse<IKanbanComment[]>> => {
    const res = await api.get<IKanbanResponse<IKanbanComment[]>>(
      `/api/v1/kanban/cards/${cardId}/comments/`,
    );
    return res.data;
  };

  const createComment = async (
    cardId: number,
    data: ICreateCommentData,
  ): Promise<IKanbanResponse<IKanbanComment>> => {
    const res = await api.post<IKanbanResponse<IKanbanComment>>(
      `/api/v1/kanban/cards/${cardId}/comments/`,
      data,
    );
    return res.data;
  };

  // Atividades (log)
  const getAtividades = async (cardId: number): Promise<IKanbanResponse<IKanbanAtividade[]>> => {
    const res = await api.get<IKanbanResponse<IKanbanAtividade[]>>(
      `/api/v1/kanban/cards/${cardId}/atividades/`,
    );
    return res.data;
  };

  // Anexos
  const getAnexos = async (cardId: number): Promise<IKanbanResponse<IKanbanAnexo[]>> => {
    const res = await api.get<IKanbanResponse<IKanbanAnexo[]>>(
      `/api/v1/kanban/cards/${cardId}/anexos/`,
    );
    return res.data;
  };

  const uploadAnexo = async (
    cardId: number,
    file: File,
  ): Promise<IKanbanResponse<IKanbanAnexo>> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post<IKanbanResponse<IKanbanAnexo>>(
      `/api/v1/kanban/cards/${cardId}/anexos/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data;
  };

  const deleteAnexo = async (anexoId: number): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.delete<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/anexos/${anexoId}/`,
    );
    return res.data;
  };

  // Campo Definicoes (Custom Fields)
  const getCampoDefinicoes = async (
    projetoId: number,
  ): Promise<IKanbanResponse<IKanbanCampoDefinicao[]>> => {
    const res = await api.get<IKanbanResponse<IKanbanCampoDefinicao[]>>(
      `/api/v1/kanban/projetos/${projetoId}/campos/`,
    );
    return res.data;
  };

  const createCampoDefinicao = async (
    projetoId: number,
    data: ICreateCampoData,
  ): Promise<IKanbanResponse<IKanbanCampoDefinicao>> => {
    const res = await api.post<IKanbanResponse<IKanbanCampoDefinicao>>(
      `/api/v1/kanban/projetos/${projetoId}/campos/`,
      data,
    );
    return res.data;
  };

  const updateCampoDefinicao = async (
    projetoId: number,
    campoId: number,
    data: IUpdateCampoData,
  ): Promise<IKanbanResponse<IKanbanCampoDefinicao>> => {
    const res = await api.put<IKanbanResponse<IKanbanCampoDefinicao>>(
      `/api/v1/kanban/projetos/${projetoId}/campos/${campoId}/`,
      data,
    );
    return res.data;
  };

  const deleteCampoDefinicao = async (
    projetoId: number,
    campoId: number,
  ): Promise<IKanbanResponse<{ message: string }>> => {
    const res = await api.delete<IKanbanResponse<{ message: string }>>(
      `/api/v1/kanban/projetos/${projetoId}/campos/${campoId}/`,
    );
    return res.data;
  };

  const reorderCampoDefinicoes = async (
    projetoId: number,
    campoIds: number[],
  ): Promise<IKanbanResponse<IKanbanCampoDefinicao[]>> => {
    const res = await api.put<IKanbanResponse<IKanbanCampoDefinicao[]>>(
      `/api/v1/kanban/projetos/${projetoId}/campos/reorder/`,
      { campo_ids: campoIds },
    );
    return res.data;
  };

  const getUsuariosMencao = async (
    search: string,
  ): Promise<
    IKanbanResponse<{ id: number; nome: string; email: string; imagem_url?: string }[]>
  > => {
    const res = await api.get('/api/v1/kanban/usuarios-mencao/', {
      params: { search },
    });
    return res.data as any;
  };

  const getColunaPermissoes = async (): Promise<IKanbanResponse<IColunaPermMap>> => {
    const res = await api.get<IKanbanResponse<IColunaPermMap>>(
      '/api/v1/kanban/evento-coluna-permissoes/me/',
    );
    return res.data;
  };

  return {
    // Projects
    getProjects,
    getEstatutoProjeto,
    createProject,
    updateProject,
    archiveProject,
    // Board
    getBoard,
    // Columns
    createColumn,
    updateColumn,
    moveColumn,
    archiveColumn,
    // Cards
    createCard,
    updateCard,
    moveCard,
    archiveCard,
    arquivarCard,
    desarquivarCard,
    getCardsArquivados,
    // Users
    getUsersByDepartamentos,
    // Comments
    getComments,
    createComment,
    // Anexos
    getAnexos,
    uploadAnexo,
    deleteAnexo,
    // Atividades
    getAtividades,
    // Campo Definicoes
    getCampoDefinicoes,
    createCampoDefinicao,
    updateCampoDefinicao,
    deleteCampoDefinicao,
    reorderCampoDefinicoes,
    // Menções (@)
    getUsuariosMencao,
    // Permissões por coluna (eventos)
    getColunaPermissoes,
  };
};
